import { ErrorMessages, MessageField, type UserType } from '@/constants';
import type { FailureResponse, GaEventBaseParams, IUserInfo } from '@/interfaces';
import { userInfoModify } from '@/utils/umsDataModify.utils';
import getConfig from 'next/config';
import { umsFetcher } from './base.fetchers';

export interface IGetUserInfoSuccessfulResponse {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url: string | null;
  user_type: UserType;
  company_name: string;
  authentication_method: string;
  opt_in_date: string | null;
  default_opt_in: boolean;
  email_sync_opt_in: boolean;
}

type GetUserInfo = ({ accessToken }: { accessToken: string }) => Promise<IUserInfo | FailureResponse | null>;

export const getUserInfo: GetUserInfo = async ({ accessToken }) => {
  const { publicRuntimeConfig } = getConfig();
  try {
    /**
     * - { accessToken, refreshToken } is custom config to handle 401 error
     * - Using fetch instead of umsFetcher since this fetcher is called in the server side, we need to handle refresh token in a different way
     */
    const res = await fetch(`${publicRuntimeConfig.pmUmsUrl}/api/v1/users/me/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (res.status === 401) {
      return Promise.reject(res);
    }

    const data = await res.json();
    return userInfoModify(data);
  } catch (error: any) {
    console.error('v1/users/me', error);
    throw error;
  }
};

type UpdateUserInfo = {
  formData: any;
};

export const updateUserInfo = async ({
  formData,
}: UpdateUserInfo): Promise<IGetUserInfoSuccessfulResponse | FailureResponse> => {
  try {
    const res = await umsFetcher.put('users/update-info', formData);
    const data = await res.json();

    const isValidUser = !!data.id;

    if (!isValidUser) {
      return ErrorMessages[MessageField.UPDATE_USER_INFO_ERROR];
    }

    return data;
  } catch (error: any) {
    console.error('users/update-info', error);
    return ErrorMessages[MessageField.UPDATE_USER_INFO_ERROR];
  }
};

export type UpdateUserNotificationsPayload = {
  accessToken?: string;
  subscribeToEmailSummary?: boolean; // Parcel Summary Emails
  defaultOptIn?: boolean; // Individual Parcel Emails
  newsletterOptIn?: boolean; // Marketing Emails
} & GaEventBaseParams;

export const updateUserNotifications = async ({
  accessToken,
  subscribeToEmailSummary,
  defaultOptIn,
  newsletterOptIn,
  clientId,
  sessionId,
  eventTimestamp,
}: UpdateUserNotificationsPayload) => {
  try {
    const response = await umsFetcher.patch(
      'v1/users/me/notifications',
      {
        subscribe_to_email_summary: subscribeToEmailSummary,
        default_opt_in: defaultOptIn,
        newsletter_opt_in: newsletterOptIn,
        ga_event: {
          client_id: clientId,
          session_id: sessionId,
          event_timestamp: eventTimestamp,
        },
      },
      { accessToken },
    );

    const data = await response.json();

    if (response.ok) {
      return Promise.resolve(data);
    }

    return Promise.reject(data);
  } catch (error) {
    console.error('v1/users/me/notifications ', error);
    throw error;
  }
};

export const deleteAccount = async ({ clientId, sessionId, eventTimestamp }: GaEventBaseParams) => {
  try {
    const res = await umsFetcher.delete('v1/users/me', {
      ga_event: {
        client_id: clientId,
        session_id: sessionId,
        event_timestamp: eventTimestamp,
      },
    });

    return await res.json();
  } catch (error: any) {
    console.error('api/v1/users/me Delete Account ', error);
    throw error;
  }
};
